import { useState, useEffect, useRef, useCallback } from 'react';
import { endOfTime, startOfTime, subTime, TimeUnit } from 'utils/dateUtils';
import {
  ICalendarValue,
  IStaticRangeOption,
  UseDatePickerHooksProps,
} from '../type/datePickerType';
import { staticDateRangeActiveStyles } from '../style/style';

const useDatePickerHooks = ({
  startDate,
  endDate,
  dateChangeHandler = () => {},
  disabled = false,
  defaultStaticRange,
  setDateRange,
  selectRange,
  setStartDateToNull = false,
}: UseDatePickerHooksProps) => {
  const [showCalendar, setShowCalendar] = useState<boolean>(false);
  const [date, setDate] = useState<ICalendarValue>(
    endDate
      ? [startDate ?? null, endDate]
      : (startDate ??
          (setStartDateToNull ? null : selectRange ? null : new Date())),
  );
  const anchorElRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<string | undefined>(defaultStaticRange);

  useEffect(() => {
    if (startDate && endDate) {
      setDate([startDate, endDate]);
    } else if (startDate && !endDate) {
      setDate(startDate);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (defaultStaticRange) onStaticDateRangeChange(defaultStaticRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultStaticRange]);

  const toggleCalendar = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      if (disabled) return;
      setShowCalendar((prev) => !prev);
    },
    [disabled],
  );

  const handleDateChange = useCallback(
    (newDate: ICalendarValue) => {
      setDate(newDate);
      setShowCalendar(false);
      dateChangeHandler(newDate);
    },
    [dateChangeHandler],
  );

  const handleClickAway = useCallback(() => {
    setShowCalendar(false);
  }, []);

  const handleActive = (value: string) =>
    value === active ? staticDateRangeActiveStyles : {};

  const onStaticDateRangeChange = useCallback(
    (range: IStaticRangeOption) => {
      if (setDateRange) setDateRange(range);

      const current = new Date();
      let start: Date | null = null;
      let end: Date | null = current;

      switch (range) {
        case 'last_7':
          setActive('last_7');
          start = subTime(current, 7, TimeUnit.DAYS);
          break;
        case 'last_15':
          setActive('last_15');
          start = subTime(current, 15, TimeUnit.DAYS);
          break;
        case 'last_30':
          setActive('last_30');
          start = subTime(current, 30, TimeUnit.DAYS);
          break;
        case 'last_90':
          setActive('last_90');
          start = subTime(current, 90, TimeUnit.DAYS);
          break;
        case 'last_180':
          setActive('last_180');
          start = subTime(current, 180, TimeUnit.DAYS);
          break;
        case 'last_365':
          setActive('last_365');
          start = subTime(current, 365, TimeUnit.DAYS);
          break;
        case 'this_month':
          setActive('this_month');
          start = startOfTime(current, TimeUnit.MONTHS);
          end = endOfTime(current, TimeUnit.MONTHS);
          break;
        case 'last_month': {
          setActive('last_month');
          const lastMonthDate = subTime(current, 1, TimeUnit.MONTHS);
          start = startOfTime(lastMonthDate, TimeUnit.MONTHS);
          end = endOfTime(lastMonthDate, TimeUnit.MONTHS);
          break;
        }
        case 'last_6_months': {
          setActive('last_6_months');
          const lastSixMonthDate = subTime(current, 6, TimeUnit.MONTHS);
          start = startOfTime(lastSixMonthDate, TimeUnit.MONTHS);
          break;
        }
        case 'to_date':
          setActive('to_date');
          start = null;
          end = null;
          break;
        default:
          break;
      }

      setDate([start, end]);
      dateChangeHandler([start, end]);
      setShowCalendar(false);
    },
    [dateChangeHandler, setDateRange],
  );

  return {
    showCalendar,
    date,
    anchorElRef,
    toggleCalendar,
    handleDateChange,
    handleClickAway,
    handleActive,
    onStaticDateRangeChange,
  };
};

export default useDatePickerHooks;
